<template>
    <div class="Settings">
        <!--
        Triggered when user requests navigation to a specified page.
        This could come from the Topbar, Breadcrumbs, or Sidebar

        @event navigate
        @property {object} to The Vue router object or other flag used to trigger navigation
    -->
        <RPTopbar
            :tabs="tabs"
            :disabled-tabs="disabledTabs"
            :active-tab="activeTab"
            :dynamic-tab="dynamicTab"
            :status="status"
            class="Settings__topbar"
            @navigate="to => $emit('navigate', to)"
        />
        <div class="Settings__header-bar">
            <div
                :class="breadcrumbsClass"
            >
                <slot name="subheader">
                    <template v-if="!isLoading">
                        <template
                            v-for="(crumb, index) in breadcrumbs"
                        >
                            <a
                                v-if="crumb.to"
                                :key="`${crumb.label}-${index}`"
                                class="Settings__breadcrumb-link"
                                @click="$emit('navigate', crumb.to)"
                            >{{ crumb.label }}</a>
                            <span
                                v-else
                                :key="`${crumb.label}-${index}`"
                                class="Settings__breadcrumb-text"
                            >{{ crumb.label }}</span>
                            <span
                                v-if="index < breadcrumbs.length - 1"
                                :key="`${crumb.label}-slash-${index}`"
                                class="Settings__breadcrumb-slash"
                            >/</span>
                        </template>
                    </template>
                </slot>
            </div>
            <div
                :class="headerClass"
            >
                <template v-if="!isLoading">
                    <!-- @slot Header slot used to replace default styling of the default Settings Layout header-->
                    <slot name="header">
                        <h3
                            class="Settings__header-text"
                        >
                            {{ header }}
                        </h3>
                    </slot>
                </template>
            </div>
        </div>
        <div class="Settings__sidebar-container">
            <RPSidebar
                :tabs="tabs"
                :active-tab="activeTab"
                :status="status"
                :disabled-tabs="disabledTabs"
                :dynamic-tab="dynamicTab"
                class="Settings__sidebar"
                @navigate="to => $emit('navigate', to)"
            />
        </div>
        <div class="Settings__content">
            <!-- @slot Default slot used to contain main Settings Layout content-->
            <slot />
        </div>
    </div>
</template>
<script>
import RPSidebar from 'components/Complex/Navs/RPSidebar.vue';
import RPTopbar from 'components/Complex/Navs/RPTopbar.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import has from 'lodash/has';

const CrumbValidator = (crumbs) => {
    return crumbs.filter((c => {
        return has(c, 'label');
    })).length === crumbs.length;
};

export default {
    name: "RPSettingsLayout",
    components: {
        RPSidebar,
        RPTopbar
    },
    props: {
        /**
        * The header for the page ex) Competitor Location Settings
        */
        header: {
            type: String,
            default: '',
        },
        /**
        * * Breadcrumbs for nested navigation.  Every object in the array must be formatted {to: {name: '<Route Name>', label: '<Route Label>'}},
        * except the final object which is not an active link.  The `to` is used by the Vuex Router,
        * so could also include any relevant params or querys, but must at least include the route name
        * as specified by the routes.js file.  The `label` is what is displayed in breadcrumb.
        * */
        breadcrumbs: {
            type: Array,
            default: () => [],
            validator: CrumbValidator
        },
        /**
        * Primary navigation object.  Every object in the array must be formatted {to: {name: '<Route Name>', label: '<Route Label>'}}.
        * The `to` is used by the Vuex Router so could also include any relevant params or queries, but must at least include the route name
        * as specified by the routes.js file.  The `label` is what is displayed in NavTab.
        */
        tabs: {
            type: Array,
            required: true,
        },
        /**
        * For dynamically disabling specific tabs by route name
        */
        disabledTabs: {
            type: Array,
            default: () => ([]),
        },
        /**
        * Route name of the current active tab typically $route.name
        */
        activeTab: {
            type: String,
            required: true,
        },
        /**
        * Dynamic NavTab label.  Appears below active tab, used to specificy dynamic page values, such a current brand name.  Not an active link
        */
        dynamicTab: {
            type: String,
            default: '',
        },
        /**
        * Component Status (see status/index.js for list of statuses)
        */
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        breadcrumbsClass(){
            return statusBuilder('Settings__breadcrumb', this.status);
        },
        headerClass(){
            return statusBuilder('Settings__header', this.status);
        },
    },
};
</script>
<style lang="scss">
    .Settings {
        display: grid;
        grid-gap: 1rem;

        @media(min-width: $max-md-width){
            grid-template-columns: 1fr 5fr;
            &__sidebar {
                width: inherit;
            }
            &__topbar {
                display: none;
            }
            &__header-bar{
                grid-column: span 3;
            }
        }
        @media(max-width: $max-md-width){
            grid-template-columns: auto;
            &__sidebar {
                display: none;
            }
            &__topbar {
                width: 7rem;
            }
        }
        &__breadcrumb {
            height: 1rem;
            margin-bottom: 1rem;
            color: $grey;

            @include loading(1rem, 13rem);

            &-link {
                text-decoration: none;
                color: #468DD3;
                &:hover {
                    cursor: pointer;
                    text-decoration: underline;
                }
            }
            &-text {
                color: $grey;
            }
            &-slash {
                color: $grey;
                margin: 0 0.5rem
            }
        }
        &__header{
            @include loading(2rem, 30rem);
        }
    }
</style>

<docs>
    ```[import](./examples/RPSettingsLayoutExample.vue)
    ```
</docs>
